import React, { useState } from "react";
import { X, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { useSubscription } from "@/context/SubscriptionContext";
import { BrandMark } from "@/components/BrandMark";

const PRO_HIGHLIGHTS = [
  "Deeper Soul Compass insights and evolution over time",
  "Full N8 Guide personalization across your whole journey",
  "Soul Pattern Insights across journal, rituals, and Soul Work",
  "Intelligent Journal — recurring theme detection and monthly reflections",
  "Personalized Ritual Engine, adaptive to what you're becoming",
  "Advanced Becoming Insights and progress trends",
  "Becoming Gallery — a visual record of your evolution",
  "Longevity & aesthetic journey tracking",
  "Intelligent Reminders that learn your rhythm",
  "Monthly Becoming Reflection",
];

export default function PricingModal() {
  const { showPricing, closePricing, pricingContext, pricing, upgrade, isPro, tier } = useSubscription();
  const [plan, setPlan] = useState("monthly");
  const [busy, setBusy] = useState(false);

  if (!showPricing || !pricing) return null;

  const monthly = pricing.monthly;
  const annual = pricing.annual;
  const selected = plan === "annual" ? annual : monthly;

  const doUpgrade = async () => {
    setBusy(true);
    try {
      await upgrade(plan);
      toast.success("Welcome to N8One Pro.");
    } catch {
      toast.error("Could not start Pro — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="pricing-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8"
    >
      <div
        className="absolute inset-0 bg-[hsl(var(--fg))]/45 backdrop-blur-sm"
        onClick={closePricing}
        aria-hidden
      />
      <div
        className="relative w-full max-w-2xl rounded-lg border hairline bg-[hsl(var(--paper))] shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-title"
      >
        <button
          type="button"
          onClick={closePricing}
          data-testid="pricing-close"
          aria-label="Close"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-[hsl(var(--sand))]/60 tab-link"
        >
          <X size={16} />
        </button>

        <div className="p-8 md:p-10">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
            <Sparkles size={12} />
            <span className="inline-flex items-center gap-1.5"><BrandMark className="text-sm" signOnMount={false} testid="pricing-modal-brand" /> Pro</span>
          </div>
          <h2
            id="pricing-title"
            className="font-display text-3xl md:text-4xl mt-2 leading-[1.1] tracking-tight"
            data-testid="pricing-title"
          >
            {pricingContext?.title || "Go deeper into your journey."}
          </h2>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[hsl(var(--fg))]/85">
            {pricingContext?.blurb ||
              "You already have what you need to begin. Pro adds depth — evolving Compass insights, a guide that learns your journey, pattern recognition across your reflections, and monthly Becoming reflections that meet you where you are."}
          </p>

          {/* Plan toggle */}
          <div
            role="tablist"
            aria-label="Choose a plan"
            className="mt-6 inline-flex p-1 rounded-full border hairline bg-[hsl(var(--sand))]/40"
          >
            <PlanPill on={plan === "monthly"} onClick={() => setPlan("monthly")} testid="plan-monthly">
              Monthly · {monthly.display}
            </PlanPill>
            <PlanPill on={plan === "annual"} onClick={() => setPlan("annual")} testid="plan-annual">
              Annual · {annual.display}
              {annual.savings_pct ? (
                <span className="ml-2 text-[10px] text-[hsl(var(--terracotta))]">save {annual.savings_pct}%</span>
              ) : null}
            </PlanPill>
          </div>

          <ul className="mt-6 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-[hsl(var(--fg))]/85">
            {PRO_HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-start gap-2">
                <Check size={14} className="mt-0.5 text-[hsl(var(--terracotta))] shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex items-center gap-4 flex-wrap">
            {isPro ? (
              <div
                data-testid="pricing-already-pro"
                className="text-sm text-[hsl(var(--muted-foreground))]"
              >
                You&rsquo;re already on Pro
                {tier === "pro" ? " — thanks for going deeper." : "."}
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={doUpgrade}
                  disabled={busy}
                  data-testid="pricing-upgrade"
                  className="rounded-full px-6 py-3 bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] text-sm hover:bg-[hsl(var(--terracotta))]/90 tab-link disabled:opacity-60"
                >
                  {busy ? "Starting Pro…" : `Start Pro · ${selected.display}`}
                </button>
                <button
                  type="button"
                  onClick={closePricing}
                  data-testid="pricing-maybe-later"
                  className="rounded-full px-5 py-2.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
                >
                  Maybe later
                </button>
              </>
            )}
          </div>

          <p className="mt-4 text-xs text-[hsl(var(--muted-foreground))]">
            Cancel anytime. Your journey, journal, rituals, and photos always stay yours.
          </p>
        </div>
      </div>
    </div>
  );
}

function PlanPill({ on, onClick, children, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 rounded-full text-sm tab-link ${
        on
          ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))]"
          : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
      }`}
    >
      {children}
    </button>
  );
}
